// Admin API client — uses the same JWT as the regular user session.
// All endpoints require an `administrator` role on the WP user.
import { safeReadJson, API } from "@/lib/api";
const AUTH_KEY = "geovinum.auth.v1";

// Netlify Edge proxies /api/* to Railway with a hard ~26s timeout, which
// kills long-running admin bridge calls (LLM generation, RAG upload, batch
// translations) at exactly ~26s → 504. For these paths we bypass Netlify
// and hit the Railway backend directly (CORS is already whitelisted for
// https://geovinum.io on the Railway service). Non-bridge admin paths keep
// the same-origin behaviour so nothing else changes.
// Hardcoded fallback = the well-known Railway URL. This protects against
// Netlify builds that ship WITHOUT REACT_APP_BACKEND_DIRECT_URL (which
// silently downgrades to same-origin → Netlify quirks → empty responses),
// AND against Netlify builds shipping a WRONG env value (e.g. a URL copied
// from another Emergent project, or a hostname without scheme → interpreted
// as a relative path by fetch → SPA fallback returns HTML → empty list).
const HARDCODED_BRIDGE_URL = "https://geovinumio-production.up.railway.app";
function _validBridgeUrl(v) {
  if (!v || typeof v !== "string") return null;
  let s = v.trim().replace(/\/+$/, "");
  if (!s) return null;
  // Auto-add scheme if missing (env values are often typed as bare hostnames).
  if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
  try {
    const host = new URL(s).hostname.toLowerCase();
    // Only accept hosts that clearly belong to this project. Anything else
    // (e.g. "neguim-studio-production.up.railway.app" leftover from another
    // Emergent project) is ignored to prevent silent breakage.
    if (!host.includes("geovinum")) {
      if (typeof console !== "undefined") {
        console.warn(`[adminApi] Ignoring REACT_APP_BACKEND_DIRECT_URL "${v}" — hostname "${host}" does not belong to geovinum. Falling back to ${HARDCODED_BRIDGE_URL}.`);
      }
      return null;
    }
    return s;
  } catch {
    return null;
  }
}
const BRIDGE_DIRECT_URL =
  _validBridgeUrl(process.env.REACT_APP_BACKEND_DIRECT_URL) || HARDCODED_BRIDGE_URL;

function isProdHost() {
  if (typeof window === "undefined") return false;
  const h = (window.location.hostname || "").toLowerCase();
  return h === "geovinum.io" || h === "www.geovinum.io" || h.endsWith(".netlify.app");
}

function resolveBase(path) {
  // Only bypass Netlify on production and only for the slow bridge routes.
  if (isProdHost() && BRIDGE_DIRECT_URL && path.startsWith("/admin/app-bridge/")) {
    return BRIDGE_DIRECT_URL;
  }
  return API;
}

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

export { API, authHeaders };

async function req(path, { method = "GET", body, isForm = false } = {}) {
  const headers = { Accept: "application/json", ...authHeaders() };
  if (!isForm) headers["Content-Type"] = "application/json";
  const init = { method, headers };
  if (body !== undefined) init.body = isForm ? body : JSON.stringify(body);
  const base = resolveBase(path);
  const r = await fetch(`${base}/api${path}`, init);
  const data = await safeReadJson(r);
  // Empty or non-JSON responses need to be tolerated on 2xx: some endpoints
  // legitimately return 204/empty body (delete ops, empty lists proxied via
  // Netlify edge that occasionally strips small JSON arrays). Only bubble up
  // as an error when the HTTP status itself signals failure.
  if (data?._empty || data?._raw) {
    if (r.ok) {
      // 200 + empty/non-JSON: return an empty structure so callers using
      // .filter/.map/.items keep working. Warn once to the console so the
      // admin can spot a truly broken upstream during debug.
      if (data?._raw) {
        console.warn(`[adminApi] ${path} returned HTTP ${r.status} with non-JSON body (${(data._raw || "").slice(0, 100)}). Returning [] as fallback.`);
      }
      return [];
    }
    const err = new Error(
      r.status >= 500
        ? `Serveur indisponible (HTTP ${r.status}). Vérifiez que le backend cible est en ligne.`
        : `Requête échouée (HTTP ${r.status}).`
    );
    err.status = r.status;
    throw err;
  }
  if (!r.ok) {
    // FastAPI puts validation errors in `detail` as an array of objects; the
    // rest of the app expects a string. Flatten intelligently so error UIs
    // don't render "[object Object]".
    let msg;
    const d = data?.detail;
    if (Array.isArray(d)) {
      msg = d.map((e) => (typeof e === "string" ? e : (e?.msg || JSON.stringify(e)))).join(" · ");
    } else if (typeof d === "string") {
      msg = d;
    } else if (d && typeof d === "object") {
      msg = d.msg || d.error || JSON.stringify(d);
    } else {
      msg = data?.error || data?.message || `Request failed (${r.status})`;
    }
    const err = new Error(msg);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const adminApi = {
  me: () => req("/admin/me"),
  stats: () => req("/admin/stats/summary"),
  statsBreakdown: (period = "month") => req(`/admin/stats/breakdown?period=${encodeURIComponent(period)}`),
  getCrmGoals: () => req("/admin/crm/goals"),
  updateCrmGoals: (payload) => req("/admin/crm/goals", { method: "PUT", body: payload }),

  // Producer lead queue — populated by zero-result public searches
  // (⌘K CommandBar + `/blog` search bar). Used by AdminProducerLeadsPage
  // to prioritise the next batch of Atlas producer fiches.
  listProducerLeads: (status = "all", kind_intent = "all", limit = 200) =>
    req(`/admin/producer-leads?status=${encodeURIComponent(status)}&kind_intent=${encodeURIComponent(kind_intent)}&limit=${limit}`),
  setProducerLeadStatus: (key, status) =>
    req(`/admin/producer-leads/${encodeURIComponent(key)}/status`, {
      method: "POST",
      body: { status },
    }),
  deleteProducerLead: (key) =>
    req(`/admin/producer-leads/${encodeURIComponent(key)}`, { method: "DELETE" }),
  // Direct-generate a producer fiche from a lead (uses the same pipeline as
  // AdminAppAtlasPage → producers/ai-generate → save).
  aiGenerateProducer: (payload) =>
    req("/admin/app-bridge/atlas/producers/ai-generate", { method: "POST", body: payload }),
  upsertProducer: (payload) =>
    req("/admin/app-bridge/atlas/producers", { method: "POST", body: payload }),

  // Contacts
  listContacts: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const q = qs.toString();
    return req(`/admin/contacts${q ? `?${q}` : ""}`);
  },
  createContact: (payload) => req("/admin/contacts", { method: "POST", body: payload }),
  getContact: (id) => req(`/admin/contacts/${id}`),
  updateContact: (id, payload) => req(`/admin/contacts/${id}`, { method: "PATCH", body: payload }),
  deleteContact: (id) => req(`/admin/contacts/${id}`, { method: "DELETE" }),
  bulkDeleteContacts: (ids) => req("/admin/contacts/bulk-delete", { method: "POST", body: { ids } }),
  bulkStartSequence: (ids) => req("/admin/contacts/bulk-start-sequence", { method: "POST", body: { ids } }),

  importContactsCsv: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return req("/admin/contacts/import", { method: "POST", body: fd, isForm: true });
  },

  listContactCategories: () => req("/admin/contacts/categories"),

  // Prospect sequence
  getProspectSequence: (id) => req(`/admin/contacts/${encodeURIComponent(id)}/sequence`),
  startProspectSequence: (id, payload = {}) => req(`/admin/contacts/${encodeURIComponent(id)}/sequence/start`, { method: "POST", body: JSON.stringify(payload) }),
  stopProspectSequence: (id) => req(`/admin/contacts/${encodeURIComponent(id)}/sequence/stop?reason=manual`, { method: "POST" }),
  markProspectReplied: (id) => req(`/admin/contacts/${encodeURIComponent(id)}/sequence/mark-replied`, { method: "POST" }),
  autostartAllProspects: () => req(`/admin/contacts/sequence/autostart-all`, { method: "POST" }),

  // Actions
  addAction: (contactId, payload) =>
    req(`/admin/contacts/${contactId}/actions`, { method: "POST", body: payload }),
  deleteAction: (id) => req(`/admin/actions/${id}`, { method: "DELETE" }),

  // Gelato — Etsy store sync + product catalog
  listGelatoStores: () => req("/admin/gelato/stores"),
  syncGelatoStore: (storeId) =>
    req(`/admin/gelato/stores/${storeId}/sync`, { method: "POST" }),
  listGelatoProducts: () => req("/admin/gelato/products"),
  overrideVariantPrice: (productId, variantId, retailCents) =>
    req(`/admin/gelato/products/${productId}/variants/${variantId}/price`, {
      method: "PATCH",
      body: { retail_amount_cents: retailCents, manual_override: true },
    }),
  setProductPublished: (productId, published) =>
    req(`/admin/gelato/products/${productId}/publish`, {
      method: "PATCH",
      body: { published },
    }),
  deleteGelatoProduct: (productId) =>
    req(`/admin/gelato/products/${productId}`, { method: "DELETE" }),
  restoreGelatoProduct: (productId) =>
    req(`/admin/gelato/products/${productId}/restore`, { method: "POST" }),
  listHiddenGelatoProducts: () => req("/admin/gelato/hidden"),

  // app.geovinum.io bridge — unified back-office
  appBridgeHealth: () => req("/admin/app-bridge/health"),
  appBridgeMetrics: () => req("/admin/app-bridge/metrics"),
  // Phase 2 — users
  listAppUsers: ({ query = "", plan = "", role = "", limit = 100 } = {}) => {
    const qs = new URLSearchParams();
    if (query) qs.set("query", query);
    if (plan) qs.set("plan", plan);
    if (role) qs.set("role", role);
    if (limit) qs.set("limit", String(limit));
    return req(`/admin/app-bridge/users?${qs.toString()}`);
  },
  setAppUserPlan: (userId, plan, months = 1) =>
    req(`/admin/app-bridge/users/${userId}/plan`, { method: "POST", body: { plan, months } }),
  banAppUser: (userId, banned = true) =>
    req(`/admin/app-bridge/users/${userId}/ban`, { method: "POST", body: { banned } }),
  creditAppUser: (userId, amount) =>
    req(`/admin/app-bridge/users/${userId}/credits`, { method: "POST", body: { amount } }),
  deleteAppUser: (userId) =>
    req(`/admin/app-bridge/users/${userId}`, { method: "DELETE" }),
  bulkDeleteAppUsers: (userIds) =>
    req(`/admin/app-bridge/users/bulk-delete`, { method: "POST", body: { user_ids: userIds } }),

  // ── Shop editorial meta (Phase 1 "anti-déperdition") ────────────────
  listShopBadges: () => req(`/admin/shop/badges`),
  getShopMeta: (productId) => req(`/admin/shop/meta/${productId}`),
  saveShopMeta: (productId, payload) =>
    req(`/admin/shop/meta/${productId}`, { method: "PUT", body: payload }),
  deleteShopMeta: (productId) =>
    req(`/admin/shop/meta/${productId}`, { method: "DELETE" }),
  getCarteDuMois: () => req(`/admin/shop/carte-du-mois`),
  saveCarteDuMois: (payload) =>
    req(`/admin/shop/carte-du-mois`, { method: "PUT", body: payload }),
  exportAppUsersUrl: (fmt) => {
    // Returns a URL clients can use with an `Authorization: Bearer` fetch.
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/admin/app-bridge/users/export.${fmt}`;
  },
  // Phase 3 — subscriptions + claims
  listAppSubscriptions: ({ plan = "", status = "", limit = 200 } = {}) => {
    const qs = new URLSearchParams();
    if (plan) qs.set("plan", plan);
    if (status) qs.set("status", status);
    if (limit) qs.set("limit", String(limit));
    return req(`/admin/app-bridge/subscriptions/list?${qs.toString()}`);
  },
  appSubscriptionsStats: () => req("/admin/app-bridge/subscriptions/stats"),
  listAppClaims: () => req("/admin/app-bridge/claims"),
  decideAppClaim: (claimId, decision, note = "") =>
    req(`/admin/app-bridge/claims/${claimId}/decision`, {
      method: "POST",
      body: { decision, note },
    }),
  // Phase 4 — Atlas (Appellations, Cépages, Producteurs)
  listAppAppellations: () => req("/admin/app-bridge/atlas/appellations?limit=1000"),
  listAppCepages: () => req("/admin/app-bridge/atlas/cepages?limit=1000"),
  listAppProducers: () => req("/admin/app-bridge/atlas/producers?limit=500"),
  wsetMasterList: () => req("/admin/app-bridge/atlas/wset-master-list"),
  upsertAppAppellation: (payload) =>
    req("/admin/app-bridge/atlas/appellations", { method: "POST", body: payload }),
  upsertAppCepage: (payload) =>
    req("/admin/app-bridge/atlas/cepages", { method: "POST", body: payload }),
  upsertAppProducer: (payload) =>
    req("/admin/app-bridge/atlas/producers", { method: "POST", body: payload }),

  // Atlas Blocks Composer (Phase 7) — categories × entities
  listAtlasBlocks: (entity_type, slug, category = "") =>
    req(`/admin/app-bridge/atlas-blocks/${entity_type}/${slug}${category ? `?category=${encodeURIComponent(category)}` : ""}`),
  updateAtlasBlock: (block_id, patch) =>
    req(`/admin/app-bridge/atlas-blocks/${block_id}`, { method: "PATCH", body: patch }),
  deleteAtlasBlock: (block_id) =>
    req(`/admin/app-bridge/atlas-blocks/${block_id}`, { method: "DELETE" }),
  reorderAtlasBlocks: (category, block_ids) =>
    req(`/admin/app-bridge/atlas-blocks/reorder`, { method: "POST", body: { category, block_ids } }),
  generateAtlasBlocks: (payload) =>
    req(`/admin/app-bridge/atlas-blocks/generate`, { method: "POST", body: payload }),
  getAtlasBlocksJob: (job_id) =>
    req(`/admin/app-bridge/atlas-blocks/jobs/${encodeURIComponent(job_id)}`),

  // SEO Prerender — bulk pre-translate cached Atlas blocks (EN/ES/PT).
  prewarmAtlasTranslations: (payload) =>
    req("/admin/atlas/prewarm-translations", { method: "POST", body: payload }),
  prewarmAtlasStatus: () => req("/admin/atlas/prewarm-translations/status"),
  deleteAppAppellation: (slug) =>
    req(`/admin/app-bridge/atlas/appellations/${encodeURIComponent(slug)}`, { method: "DELETE" }),
  deleteAppCepage: (slug) =>
    req(`/admin/app-bridge/atlas/cepages/${encodeURIComponent(slug)}`, { method: "DELETE" }),
  aiGenerateAppellation: ({ name, region = "", country = "France", extra_instructions = "" }) =>
    req("/admin/app-bridge/atlas/appellations/ai-generate", {
      method: "POST",
      body: { name, region, country, extra_instructions },
    }),
  aiGenerateCepage: ({ name, extra_instructions = "" }) =>
    req("/admin/app-bridge/atlas/cepages/ai-generate", {
      method: "POST",
      body: { name, extra_instructions },
    }),
  aiGenerateProducer: ({ name, website = "", region = "", extra_instructions = "" }) =>
    req("/admin/app-bridge/atlas/producers/ai-generate", {
      method: "POST",
      body: { name, website, region, extra_instructions },
    }),
  aiBatchAppellations: (names, language = "fr") =>
    req("/admin/app-bridge/atlas/appellations/ai-batch-generate", {
      method: "POST",
      body: { names, language },
    }),
  // Phase 5 — Academy Generator + Editor
  listAcademyCourses: (status = "") => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    qs.set("limit", "200");
    return req(`/admin/app-bridge/academy/generated?${qs.toString()}`);
  },
  getAcademyCourse: (id, lang = "fr") =>
    req(`/admin/app-bridge/academy/generated/${id}?lang=${lang}`),
  academyTranslationStatus: (id) =>
    req(`/admin/app-bridge/academy/generated/${id}/translation-status`),
  generateAcademy: ({ source_type, source_value, extra_instructions = "", language = "fr" }) =>
    req("/admin/app-bridge/academy/generate", {
      method: "POST",
      body: { source_type, source_value, extra_instructions, language },
    }),
  generateAcademyFromUrl: ({ url, extra_instructions = "" }) =>
    req("/admin/app-bridge/academy/generate-from-url", {
      method: "POST",
      body: { url, extra_instructions },
    }),
  generateAcademyRich: (payload) =>
    req("/admin/app-bridge/academy/generate-rich", { method: "POST", body: payload }),
  saveAcademyCourse: (id, payload) =>
    req(`/admin/app-bridge/academy/generated/${id}`, { method: "PUT", body: payload }),
  publishAcademy: (id) =>
    req(`/admin/app-bridge/academy/generated/${id}/publish`, { method: "POST" }),
  unpublishAcademy: (id) =>
    req(`/admin/app-bridge/academy/generated/${id}/unpublish`, { method: "POST" }),
  regenerateAcademyCover: (id) =>
    req(`/admin/app-bridge/academy/generated/${id}/regenerate-cover`, { method: "POST" }),
  retranslateAcademy: (id, lang) =>
    req(`/admin/app-bridge/academy/generated/${id}/retranslate?lang=${lang}`, { method: "POST" }),
  deleteAcademy: (id) =>
    req(`/admin/app-bridge/academy/generated/${id}`, { method: "DELETE" }),
  bulkPublishAcademy: (ids) =>
    req("/admin/app-bridge/academy/bulk-publish", { method: "POST", body: { ids } }),
  bulkUnpublishAcademy: (ids) =>
    req("/admin/app-bridge/academy/bulk-unpublish", { method: "POST", body: { ids } }),
  bulkDeleteAcademy: (ids) =>
    req("/admin/app-bridge/academy/bulk-delete", { method: "POST", body: { ids } }),
  academyBlockAiAction: ({ block, action, lang = "fr" }) =>
    req("/admin/app-bridge/academy/block/ai-action", {
      method: "POST",
      body: { block, action, lang },
    }),
  // Phase 6 — Shop orders + RAG
  listAppShopOrders: ({ status = "", limit = 100 } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set("status", status);
    qs.set("limit", String(limit));
    return req(`/admin/app-bridge/shop/orders?${qs.toString()}`);
  },
  listRagDocs: () => req("/admin/app-bridge/rag/docs"),
  ragChunks: (docId) => req(`/admin/app-bridge/rag/chunks/${docId}`),
  ragSearch: (q, k = 5) =>
    req("/admin/app-bridge/rag/search", { method: "POST", body: { q, k } }),
  ragDeleteDoc: (docId) =>
    req(`/admin/app-bridge/rag/docs/${docId}`, { method: "DELETE" }),
  ragUpload: (file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.theme_slug) fd.append("theme_slug", opts.theme_slug);
    if (opts.bundle_id) fd.append("bundle_id", opts.bundle_id);
    return req("/admin/app-bridge/rag/upload", { method: "POST", body: fd, isForm: true });
  },

  // Analytics (Phase 3 Dashboard)
  analyticsConfigStatus: () => req("/admin/analytics/config-status"),
  analyticsPeriod: (year, month) => req(`/admin/analytics/period-kpis?year=${year}&month=${month}`),
  analyticsCycles: (limit = 24) => req(`/admin/analytics/cycle-kpis?limit=${limit}`),

  // Monthly Cycle (AI Orchestrator) — manual trigger of the first-Monday cron
  monthlyCycleTriggerAuto: () => req("/admin/monthly-cycle/trigger-auto", { method: "POST" }),

  // Stripe webhook health
  stripeWebhookHealth: () => req("/admin/stripe/webhook-health"),

  // Bunny (used by Academy admin to attach videos to series)
  bunnyVideos: (page = 1, perPage = 200) => req(`/admin/bunny/videos?page=${page}&per_page=${perPage}`),

  // Academy series CRUD
  adminListSeries: () => req("/admin/academy/series"),
  adminCreateSeries: (body) => req("/admin/academy/series", { method: "POST", body }),
  adminUpdateSeries: (slug, body) => req(`/admin/academy/series/${slug}`, { method: "PATCH", body }),
  adminDeleteSeries: (slug) => req(`/admin/academy/series/${slug}`, { method: "DELETE" }),
  adminAttachVideo: (slug, body) => req(`/admin/academy/series/${slug}/videos`, { method: "POST", body }),
  adminDetachVideo: (slug, guid) => req(`/admin/academy/series/${slug}/videos/${guid}`, { method: "DELETE" }),
  adminReorderVideos: (slug, guids) => req(`/admin/academy/series/${slug}/videos/reorder`, { method: "POST", body: { guids } }),
  adminGenerateThumbnail: (slug) => req(`/admin/blog/articles/${slug}/generate-thumbnail`, { method: "POST" }),
  // Ebook (PDF upload + lead capture)
  adminEbookUpload: (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return req(`/admin/blog/articles/${slug}/ebook-upload`, { method: "POST", body: fd, isForm: true });
  },
  adminEbookDelete: (slug) => req(`/admin/blog/articles/${slug}/ebook`, { method: "DELETE" }),
  adminEbookLeads: (slug) => req(`/admin/blog/articles/${slug}/ebook-leads`),
  adminGetFeatured: () => req("/admin/academy/featured"),
  adminSetFeatured: (series_slug) => req("/admin/academy/featured", { method: "POST", body: { series_slug } }),
  academyImportFromBunny: () => req("/admin/academy/series/import-from-bunny", { method: "POST" }),

  // Blog
  blogList: (q) => req(`/admin/blog/articles${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  blogCreate: (body) => req("/admin/blog/articles", { method: "POST", body }),
  blogUpdate: (slug, body) => req(`/admin/blog/articles/${slug}`, { method: "PATCH", body }),
  blogDelete: (slug) => req(`/admin/blog/articles/${slug}`, { method: "DELETE" }),
  blogBulkDelete: (slugs) => req("/admin/blog/articles/bulk-delete", { method: "POST", body: { slugs } }),
  blogGenerateCoverNanoBanana: (slug) => req(`/admin/blog/articles/${slug}/generate-cover-nanobanana`, { method: "POST" }),
  blogTranslate: (slug, languages) => req(`/admin/blog/articles/${slug}/translate`, { method: "POST", body: { languages: languages || null } }),
  blogTranslationGroups: () => req("/admin/blog/translation-groups"),
  blogBackfillGroups: () => req("/admin/blog/backfill-translation-groups", { method: "POST" }),

  // Newsletter drafts (from monthly-theme bundles)
  newsletterDraftsList: () => req("/admin/newsletter-drafts"),
  newsletterDraftDelete: (bundle_id, language) => req(`/admin/newsletter-drafts/${bundle_id}/${language}`, { method: "DELETE" }),
  newsletterDraftSchedule: (bundle_id, body) => req(`/admin/newsletter-drafts/${bundle_id}/schedule`, { method: "POST", body }),

  // Phase 1 — Monthly Cycle (AI orchestration layer)
  monthlyCycleList: () => req("/admin/monthly-cycle"),
  monthlyCycleGet: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}`),
  monthlyCycleLaunch: () => req("/admin/monthly-cycle/launch", { method: "POST" }),
  monthlyCycleDecide: (cycle_id, body) => req(`/admin/monthly-cycle/${cycle_id}/proposal`, { method: "POST", body }),
  monthlyCycleProduce: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/produce`, { method: "POST" }),
  cycleDeliverables: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/deliverables`),
  cyclePublishAll: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/publish-all`, { method: "POST" }),
  cycleReject: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/reject`, { method: "POST" }),
  cycleDelete: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}`, { method: "DELETE" }),
  cycleRestart: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/restart`, { method: "POST" }),
  // Phase 2 — KMS sources & propose
  cycleSourcesRequest: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/sources/request`, { method: "POST" }),
  cycleKmsPropose: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/kms/propose`, { method: "POST" }),
  cycleKmsApply: (cycle_id, body) => req(`/admin/monthly-cycle/${cycle_id}/kms/apply`, { method: "POST", body }),
  cycleSourceUpload: async (cycle_id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return req(`/admin/monthly-cycle/${cycle_id}/sources/upload`, { method: "POST", body: fd, isForm: true });
  },
  // Phase 4 — Social visuals
  cycleSocialGenerate: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/social/generate`, { method: "POST" }),
  cycleSocialVisuals: (cycle_id) => req(`/admin/monthly-cycle/${cycle_id}/social/visuals`),
  cycleSocialPushContentStudio: (cycle_id, caption) => req(`/admin/monthly-cycle/social/push-contentstudio`, { method: "POST", body: { cycle_id, caption } }),
  // Apollo
  apolloSearch: (body) => req("/admin/apollo/search", { method: "POST", body }),
  apolloEnrich: (email) => req("/admin/apollo/enrich", { method: "POST", body: { email } }),
  // Admin conversational agent
  adminAgentChat: (body) => req("/admin/agent/chat", { method: "POST", body }),
  adminAgentSession: (session_id) => req(`/admin/agent/sessions/${session_id}`),

  // ContentStudio (social auto-publish)
  socialHealth: () => req("/admin/social/health"),
  socialWorkspaces: () => req("/admin/social/workspaces"),
  socialAccounts: (wsId) => req(`/admin/social/workspaces/${wsId}/accounts`),
  socialCategories: (wsId) => req(`/admin/social/workspaces/${wsId}/categories`),
  socialGetConfig: () => req("/admin/social/config"),
  socialPutConfig: (body) => req("/admin/social/config", { method: "PUT", body }),
  socialDispatch: (body) => req("/admin/social/dispatch", { method: "POST", body }),
  socialPreview: (body) => req("/admin/social/preview", { method: "POST", body }),
  socialRecent: () => req("/admin/social/posts"),
  socialListTemplates: () => req("/admin/social/templates"),
  socialUpdateTemplate: (name, body) => req(`/admin/social/templates/${name}`, { method: "PUT", body }),
  socialResetTemplate: (name) => req(`/admin/social/templates/${name}/reset`, { method: "POST" }),
  socialTemplatePreview: (body) => req("/admin/social/template_preview", { method: "POST", body }),

  // Email sequences (B2B prospect)
  emailOverview: () => req("/admin/email/overview"),
  emailActive: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return req(`/admin/email/active${qs ? `?${qs}` : ""}`);
  },
  emailHistory: (limit = 50) => req(`/admin/email/history?limit=${limit}`),
  emailRunNow: () => req("/admin/email/run-now", { method: "POST" }),
  listEmailTemplates: () => req("/admin/email/templates"),
  getEmailTemplate: (cat, lang, step) => req(`/admin/email/templates/${cat}/${lang}/${step}`),
  patchEmailTemplateStep: (cat, lang, step, body) => req(`/admin/email/templates/${cat}/${lang}/${step}/step`, { method: "PUT", body }),
  patchEmailTemplateUseCase: (cat, lang, body) => req(`/admin/email/templates/${cat}/${lang}/use-case`, { method: "PUT", body }),
  resetEmailTemplateStep: (cat, lang, step) => req(`/admin/email/templates/${cat}/${lang}/${step}/step`, { method: "DELETE" }),
  translateEmailTemplate: (body) => req("/admin/email/templates/translate", { method: "POST", body }),
  setEmailPersonalize: (cat, enabled) => req(`/admin/email/templates/personalize/${cat}`, { method: "PUT", body: { enabled } }),

  // Newsletter (GDPR)
  newsletterList: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return req(`/admin/newsletter/subscribers${qs ? `?${qs}` : ""}`);
  },
  newsletterRevoke: (id) => req(`/admin/newsletter/subscribers/${encodeURIComponent(id)}/revoke`, { method: "POST" }),
  newsletterBulkDelete: (ids) => req("/admin/newsletter/subscribers/bulk-delete", { method: "POST", body: { ids } }),
  newsletterBulkRevoke: (ids) => req("/admin/newsletter/subscribers/bulk-revoke", { method: "POST", body: { ids } }),
  newsletterImportApp: (status = "all") =>
    req(`/admin/newsletter/import-app?status=${encodeURIComponent(status)}`, { method: "POST" }),
  newsletterAddManual: (payload) =>
    req("/admin/newsletter/subscribers", { method: "POST", body: payload }),
  newsletterImportCsv: (file, sourceOverride) => {
    const fd = new FormData();
    fd.append("file", file);
    const qs = sourceOverride ? `?source_override=${encodeURIComponent(sourceOverride)}` : "";
    return req(`/admin/newsletter/import-csv${qs}`, { method: "POST", body: fd, isForm: true });
  },
  newsletterCsvTemplateUrl: () => `${API}/api/admin/newsletter/import-csv/template.csv`,
  newsletterContent: () => req("/admin/newsletter/content"),
  newsletterCampaignTest: (body) => req("/admin/newsletter/campaign/test", { method: "POST", body }),
  newsletterCampaignSend: (body) => req("/admin/newsletter/campaign/send", { method: "POST", body }),
  newsletterCampaignsHistory: (limit = 50) => req(`/admin/newsletter/campaigns?limit=${limit}`),
  newsletterExportCsvUrl: () => `${API}/api/admin/newsletter/subscribers.csv`,

  // Thematic newsletter — monthly composer + schedule queue.
  newsletterThematicPreviewUrl: (slug, opts = {}) => {
    const q = new URLSearchParams({ theme_slug: slug });
    if (opts.lang) q.set("lang", opts.lang);
    if (opts.product_id) q.set("product_id", opts.product_id);
    if (opts.with_ai !== undefined) q.set("with_ai", String(opts.with_ai));
    return `${API}/api/admin/newsletter/thematic/preview?${q.toString()}`;
  },
  newsletterThematicThemes: () => req("/admin/newsletter/thematic/themes"),
  newsletterThematicProducts: () => req("/admin/newsletter/thematic/products"),
  newsletterThematicCourses: () => req("/admin/newsletter/thematic/courses"),
  newsletterThematicScheduleBulk: (items, replaceQueued = false) =>
    req("/admin/newsletter/thematic/schedule-bulk", {
      method: "POST",
      body: { items, replace_queued: replaceQueued },
    }),
  newsletterThematicTestSend: (payload) =>
    req("/admin/newsletter/thematic/test", { method: "POST", body: payload }),
  newsletterThematicAssociate: (payload) =>
    req("/admin/newsletter/thematic/associate", { method: "POST", body: payload }),
  newsletterThematicAppFeatures: () => req("/admin/newsletter/thematic/app-features"),
  newsletterThematicDraft: (payload) =>
    req("/admin/newsletter/thematic/draft", { method: "POST", body: payload }),
  newsletterThematicPreviewPost: (payload) =>
    // Returns raw HTML — we fetch() directly instead of going through
    // the JSON `req()` helper.
    payload,
  newsletterThematicSchedule: (body) =>
    req("/admin/newsletter/thematic/schedule", { method: "POST", body }),
  newsletterThematicList: () => req("/admin/newsletter/thematic/scheduled"),
  newsletterThematicCancel: (id) =>
    req(`/admin/newsletter/thematic/scheduled/${encodeURIComponent(id)}`, { method: "DELETE" }),
  newsletterThematicSendNow: (id) =>
    req(`/admin/newsletter/thematic/scheduled/${encodeURIComponent(id)}/send-now`, { method: "POST" }),

  // Beta onboarding sequence (J+3 / J+7) — manual trigger.
  betaOnboardingTick: () => req("/admin/beta/onboarding/tick", { method: "POST" }),
  betaOnboardingStatus: () => req("/admin/beta/onboarding/status"),

  // Contact messages
  contactMessagesList: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null)).toString();
    return req(`/admin/contact-messages${qs ? `?${qs}` : ""}`);
  },
  contactMessageMarkRead: (id) => req(`/admin/contact-messages/${encodeURIComponent(id)}/mark-read`, { method: "POST" }),
  contactMessageDelete: (id) => req(`/admin/contact-messages/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // Spam cleanup — preview + purge for the two public forms
  spamNewsletterPreview: () => req(`/admin/spam/newsletter/preview`),
  spamNewsletterPurge: () => req(`/admin/spam/newsletter/purge`, { method: "POST", body: { confirm: "CONFIRM_PURGE" } }),
  spamContactPreview: () => req(`/admin/spam/contact/preview`),
  spamContactPurge: () => req(`/admin/spam/contact/purge`, { method: "POST", body: { confirm: "CONFIRM_PURGE" } }),

  // Shop reviews — moderation
  shopReviewsList: (status = "pending") => req(`/admin/shop/reviews?status=${encodeURIComponent(status)}`),
  shopReviewModerate: (reviewId, action) => req(`/admin/shop/reviews/${encodeURIComponent(reviewId)}/moderate`, { method: "POST", body: { action } }),

  // Atlas IA — unified back-office
  atlasCatalogue: () => req(`/admin/atlas-ia/catalogue`),
  atlasFiche: (kind, slug) => req(`/admin/atlas-ia/fiche/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`),
  atlasFichePatch: (kind, slug, lang, body) => req(`/admin/atlas-ia/fiche/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}?lang=${encodeURIComponent(lang)}`, { method: "PATCH", body }),
  atlasTranslateBulk: (body) => req(`/admin/atlas-ia/translate-bulk`, { method: "POST", body }),
  atlasGenerateBulk: (body) => req(`/admin/atlas-ia/generate-bulk`, { method: "POST", body }),
  atlasDuplicates: () => req(`/admin/atlas-ia/duplicates`),
  atlasMerge: (body) => req(`/admin/atlas-ia/merge`, { method: "POST", body }),
  atlasFicheLink: (kind, slug, body) => req(`/admin/atlas-ia/fiche/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}/link`, { method: "POST", body }),
  atlasFicheUnlink: (kind, slug, { target_kind, target_slug }) => req(`/admin/atlas-ia/fiche/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}/link?target_kind=${encodeURIComponent(target_kind)}&target_slug=${encodeURIComponent(target_slug)}`, { method: "DELETE" }),
  // Newsletter — per-row preview + test send
  newsletterPreviewUrl: (scheduledId) => `${API}/api/admin/newsletter/scheduled/${encodeURIComponent(scheduledId)}/preview`,
  newsletterTestSend: (scheduledId, recipient) => req(`/admin/newsletter/scheduled/${encodeURIComponent(scheduledId)}/test`, { method: "POST", body: { recipient } }),
};

// Build a deeplink to a social platform from a contact's stored URL or username.
export function socialMessageHref(contact, network) {
  const url = contact?.socials?.[network] || "";
  if (!url) return "";
  // For platforms with messaging endpoints, jump straight into the DM thread.
  if (network === "linkedin" && url.includes("linkedin.com/in/")) {
    return url.endsWith("/") ? `${url}detail/contact-info/` : `${url}/detail/contact-info/`;
  }
  return url;
}
